import { z } from 'zod';
import { CalendarDate, TaskPriority, TaskView, WorkflowCategory } from './project.js';
import { DayOffReason } from './work-schedule.js';
/**
 * View đa dự án của **chính người đang đăng nhập** (spec mục 3).
 *
 * Khác `TaskView` một điểm: có `projectName`. Ở board thì tên project là thừa
 * — cả màn hình chỉ có một project. Ở đây mỗi dòng có thể thuộc project khác
 * nhau nên không có tên thì không đọc được.
 *
 * ⚠️ Những view này **có** tiêu đề và mã task, nên chúng bắt buộc đi qua
 * repository có scope như mọi truy vấn khác (luật #5). Đừng nhầm với
 * `WorkloadDay` ở `project.ts` — chỗ đó cố ý bỏ qua scope và đổi lại chỉ được
 * trả về con số.
 */
export const MyTaskView = TaskView.extend({
    projectName: z.string(),
    projectKey: z.string(),
});
/**
 * Cách sắp xếp danh sách My Tasks.
 *
 * `focus` là mặc định — APM-ORG-h2. Nó xếp **việc đang làm** lên trước, rồi
 * việc sắp làm, rồi việc đã xong; trong mỗi nhóm thì gần hạn trước.
 *
 * ## Vì sao không để `dueDate` làm mặc định nữa
 *
 * Xếp thuần theo hạn trộn lẫn ba nhóm: một việc đã xong hôm qua đứng trên việc
 * đang làm dở của hôm nay, chỉ vì hạn của nó sớm hơn. Danh sách "việc của tôi"
 * mở ra để trả lời "giờ làm gì" — và câu đó không bao giờ có đáp án là một
 * việc đã đóng.
 */
export const MyTaskSort = z.enum(['focus', 'dueDate', 'priority', 'project', 'updatedAt']);
/**
 * Bộ lọc My Tasks. Mọi trường đều tuỳ chọn — không lọc gì thì ra tất cả.
 *
 * Nằm ở query string chứ không phải body vì frontend phải chia sẻ được link
 * tới một bộ lọc cụ thể (`frontend-guide.md` §2).
 */
const MyTaskQueryShape = z.object({
    /** Lọc theo nhóm trạng thái, không theo cột cụ thể: cột là tuỳ biến từng project. */
    status: WorkflowCategory.optional(),
    /**
     * Chỉ việc **chưa xong** (nhóm trạng thái khác `done`) — APM-MYT-e. Lịch đội
     * chỉ đếm việc chưa xong, nên bấm một con số trên lịch phải mở đúng tập ấy.
     *
     * Loại trừ với `status`: "chưa xong" **và** "đã xong" là một câu tự mâu thuẫn,
     * còn "chưa xong" và "đang làm" thì `status` một mình đã nói đủ. Gửi cả hai là
     * 400, không đoán cái nào thắng. `false` nghĩa là không lọc, **không** phải
     * "chỉ việc đã xong" — muốn thế thì `status=done`.
     *
     * ⚠️ `z.stringbool()` chứ không phải `z.coerce.boolean()`: cái sau ép **mọi**
     * chuỗi khác rỗng thành `true`, nên `?open=false` sẽ có nghĩa ngược với chữ
     * đang viết trên URL (cùng lý do với `NotificationQuery.unreadOnly`).
     */
    open: z.stringbool().optional(),
    projectId: z.uuid().optional(),
    priority: TaskPriority.optional(),
    /**
     * Chỉ lấy task quá hạn (`dueDate` < hôm nay và chưa xong). `z.stringbool()`
     * vì cùng lý do với `open` — trước APM-MYT-e `?overdue=false` lọc quá hạn.
     */
    overdue: z.stringbool().optional(),
    sort: MyTaskSort.default('focus'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
/** `open` và `status` loại trừ nhau — xem `open` ở trên. */
function openExcludesStatus(query, ctx) {
    if (query.open !== undefined && query.status !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['open'],
            message: 'open và status không dùng cùng nhau',
        });
    }
}
export const MyTaskQuery = MyTaskQueryShape.superRefine(openExcludesStatus);
/**
 * Cách sắp xếp danh sách **việc đội của tôi** — APM-MYT-a.
 *
 * Tách khỏi `MyTaskSort` thay vì thêm `assignee` vào đó: ở danh sách của chính
 * mình mọi dòng cùng một người, nên "xếp theo người" là một lựa chọn vô nghĩa
 * mà giao diện sẽ phải tự giấu đi. `/me/tasks` vì thế giữ nguyên.
 */
export const TeamTaskSort = z.enum([...MyTaskSort.options, 'assignee']);
/** Trần số dòng của danh sách việc đội — chạm trần thì `truncated`. */
export const TEAM_TASK_LIMIT = 500;
/**
 * Bộ lọc việc đội. Như `MyTaskQuery`, thêm `userId` để **thu hẹp** trong đội.
 *
 * ⚠️ `userId` không bao giờ **mở rộng** phạm vi: đội do server tự tính từ quan
 * hệ quản lý trực tiếp, và một `userId` ngoài đội nhận 403 — docs/plan-my-team-tasks.md §2.2.
 *
 * `limit` mặc định bằng trần: danh sách đội mở ra để nhìn **toàn bộ** việc của
 * mọi người, và một mặc định thấp hơn là cắt im lặng ở lần mở đầu tiên.
 */
export const TeamTaskQuery = MyTaskQueryShape.extend({
    userId: z.uuid().optional(),
    sort: TeamTaskSort.default('focus'),
    limit: z.coerce.number().int().min(1).max(TEAM_TASK_LIMIT).default(TEAM_TASK_LIMIT),
}).superRefine(openExcludesStatus);
/**
 * Một dòng việc của đội. Người được giao **luôn** có — danh sách được dựng từ
 * chính người được giao — nên hai trường ấy không `nullable` như ở `TaskView`.
 */
export const TeamTaskView = MyTaskView.extend({
    assigneeId: z.uuid(),
    assigneeName: z.string(),
});
/** Một người trong đội — để dựng bộ lọc người. */
export const TeamTaskMember = z.object({
    userId: z.uuid(),
    name: z.string(),
});
/** Một dự án đang có việc của đội — để dựng bộ lọc dự án. */
export const TeamTaskProject = z.object({
    projectId: z.uuid(),
    key: z.string(),
    name: z.string(),
});
/**
 * Phản hồi của `GET /me/team/tasks`.
 *
 * - `members` là **cả đội**, kể cả người chưa có việc nào.
 * - `projects` là mọi dự án đang có việc của đội, **không** phụ thuộc bộ lọc
 *   hiện tại: lọc theo một người mà danh sách dự án co theo thì không chọn lại
 *   được dự án khác mà không xoá bộ lọc người trước.
 * - `truncated` khi chạm `limit` — giao diện hiện dải cảnh báo.
 */
export const TeamTaskList = z.object({
    items: z.array(TeamTaskView),
    members: z.array(TeamTaskMember),
    projects: z.array(TeamTaskProject),
    truncated: z.boolean(),
});
/**
 * Chi tiết một việc của cấp dưới — `GET /me/team/tasks/:taskId`.
 *
 * `readOnly` khi người xem không có `task:update` ở việc đó — thường là vì
 * leader không là thành viên dự án. Giao diện ẩn mọi nút sửa, đổi tiến độ và
 * bình luận; cửa thật vẫn ở các endpoint ghi, vốn đi qua `scoped()`.
 *
 * Kèm `projectName`/`projectKey` (như `MyTaskView`): leader không là thành
 * viên thì không đọc được dự án qua đường nào khác để lấy tên.
 */
export const TeamTaskDetail = MyTaskView.extend({
    readOnly: z.boolean(),
});
/**
 * Lịch cá nhân — dùng chung cho cả calendar view lẫn timeline view.
 *
 * Một endpoint chứ không hai: hai màn hình đó hiển thị **cùng một tập dữ
 * liệu**, chỉ khác cách gom (theo ngày / theo project). Tách đôi thì thành hai
 * đường query phải giữ đồng bộ với nhau mà không được lợi gì.
 */
export const ScheduleQuery = z.object({
    from: CalendarDate,
    to: CalendarDate,
});
/**
 * Một mục trên lịch. Chỉ giữ những trường cần để vẽ — danh sách đầy đủ nằm ở
 * `MyTaskView`, bấm vào mới tải.
 */
export const ScheduleItem = z.object({
    id: z.uuid(),
    key: z.string(),
    title: z.string(),
    projectId: z.uuid(),
    projectName: z.string(),
    projectKey: z.string(),
    statusCategory: WorkflowCategory,
    priority: TaskPriority,
    isBlocked: z.boolean(),
    /** 0–100. Lịch hiện nó ngay trên thẻ để khỏi phải mở từng việc mới biết. */
    progressPct: z.number().int().min(0).max(100),
    startDate: CalendarDate.nullable(),
    dueDate: CalendarDate.nullable(),
    estimateHours: z.number().nullable(),
});
export const ScheduleView = z.object({
    from: CalendarDate,
    to: CalendarDate,
    items: z.array(ScheduleItem),
});
/** Trần khoảng thời gian của lịch cá nhân và lịch đội — cùng một con số cho cả hai. */
export const MAX_SCHEDULE_DAYS = 186;
/**
 * Lịch đội — APM-MYT-b, docs/plan-my-team-tasks.md §2.4. Cùng `from`/`to` với
 * lịch cá nhân, cùng trần `MAX_SCHEDULE_DAYS`.
 */
export const TeamScheduleQuery = ScheduleQuery;
/**
 * Một ô của lịch đội: người này có `count` việc **chưa xong** ở dự án này chiếm
 * ngày này. Chỉ ô `count > 0` mới có mặt.
 *
 * "Chiếm" theo đúng `occupiesDay` (`day-occupancy.ts`) — cùng luật với lịch cá
 * nhân, để con số ở đây khớp số thẻ khi mở lịch của người ấy.
 */
export const TeamScheduleCell = z.object({
    date: CalendarDate,
    userId: z.uuid(),
    projectId: z.uuid(),
    count: z.number().int().min(1),
});
/**
 * Một ngày một người không làm việc. `reason` như `DayOff` của `/me/days-off`
 * — cùng nguồn — để giao diện tự chọn: cuối tuần (`off_shift`) của cả đội là
 * chuyện ai cũng biết, còn nghỉ phép của một người mới là thứ đáng ghi "(nghỉ)".
 */
export const TeamDayOff = z.object({
    userId: z.uuid(),
    date: CalendarDate,
    reason: DayOffReason,
});
/**
 * Phản hồi của `GET /me/team/schedule`. **Chỉ con số** — không tiêu đề việc
 * (người dùng chốt "chỉ cơ bản", và nó giữ payload nhỏ).
 *
 * - `members` là cả đội, kể cả người không có việc nào trong kỳ.
 * - `projects` là những dự án **có mặt trong `cells`**.
 * - `truncated` khi số việc trong kỳ chạm trần đọc (`TEAM_SCHEDULE_TASK_LIMIT`):
 *   khi ấy các con số là **thiếu**, và giao diện phải nói ra thay vì để một ô
 *   "·2" trông như sự thật.
 */
export const TeamScheduleView = z.object({
    from: CalendarDate,
    to: CalendarDate,
    members: z.array(TeamTaskMember),
    projects: z.array(TeamTaskProject),
    cells: z.array(TeamScheduleCell),
    daysOff: z.array(TeamDayOff),
    truncated: z.boolean(),
});
/** Trần số việc lịch đội đọc trong một kỳ — chạm trần thì `truncated`. */
export const TEAM_SCHEDULE_TASK_LIMIT = 5000;
//# sourceMappingURL=me.js.map